import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { IOutfall, ComplianceStatus } from '../../../types/discharge';

// google-map-react passes lat/lng as props for positioning; they are consumed by the lib
interface IOutfallMarkerProps {
  lat: number;
  lng: number;
  outfall: IOutfall;
  isSelected: boolean;
  onClick: () => void;
}

const COMPLIANCE_COLORS: Record<ComplianceStatus, string> = {
  green: '#34d399',
  amber: '#fbbf24',
  red: '#f87171',
};

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.5); }
  70%  { box-shadow: 0 0 0 8px rgba(52, 211, 153, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
`;

const MarkerRoot = styled.div`
  position: relative;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 10;
`;

const Circle = styled.div<{ $status: ComplianceStatus; $selected: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(62, 8, 178, 0.85);
  border: 2.5px solid ${({ $status }) => COMPLIANCE_COLORS[$status]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s, box-shadow 0.15s;
  ${({ $selected, $status }) =>
    $selected &&
    css`
      transform: scale(1.25);
      box-shadow: 0 0 0 3px ${COMPLIANCE_COLORS[$status]}55;
    `}
  &:hover {
    transform: scale(1.2);
  }
  ${({ $status }) =>
    $status === 'green' &&
    css`
      animation: ${pulse} 2.4s ease-out infinite;
    `}
`;

const InnerDot = styled.div`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #fff;
  opacity: 0.9;
`;

const Tooltip = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(10, 16, 32, 0.92);
  border: 1px solid rgba(52, 211, 153, 0.25);
  border-radius: 6px;
  padding: 4px 8px;
  white-space: nowrap;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.12s;
  backdrop-filter: blur(4px);
`;

const TooltipId = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: #fff;
  font-family: 'DM Sans', -apple-system, sans-serif;
  letter-spacing: 0.03em;
`;

const TooltipType = styled.span`
  font-size: 0.62rem;
  color: #8896ac;
  font-family: 'DM Sans', -apple-system, sans-serif;
  margin-left: 5px;
`;

const OutfallMarker: React.FC<IOutfallMarkerProps> = ({ outfall, isSelected, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <MarkerRoot
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Tooltip $visible={hovered || isSelected}>
        <TooltipId>{outfall.id}</TooltipId>
        <TooltipType>{outfall.typeDesc}</TooltipType>
      </Tooltip>
      <Circle $status={outfall.complianceStatus} $selected={isSelected}>
        <InnerDot />
      </Circle>
    </MarkerRoot>
  );
};

export default OutfallMarker;
